/**
 * THE FILE TO EDIT FIRST.
 *
 * Every section of the site is described here once: its name in the sidebar, its
 * API endpoint, its table columns, and the form the admin fills in to add a row.
 * The sidebar, the pages, the tables and the admin dialog are all generated from
 * this list, so adding a section means adding one entry — no new page component.
 */

import {
  Briefcase,
  FileText,
  Landmark,
  TrendingDown,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import { CompanyCell } from "../components/CompanyLogo";
import { Pills } from "../components/table/Pills";
import { formatCount, formatDate, formatUsd, hostname, orDash } from "./format";
import type { AnyRecord } from "../types";

export type DatasetId = "funding" | "layoffs" | "investors" | "jobs" | "reports" | "news";

export type Column = {
  /** Property name on the row. Also used as the React key. */
  key: string;
  header: string;
  /** Tailwind width class, e.g. "w-48". Omit to let the column size itself. */
  width?: string;
  align?: "left" | "right";
  /** How to draw the cell. Defaults to showing the raw value. */
  render?: (row: AnyRecord) => ReactNode;
  /**
   * Value to send as `?sortBy=`. Sorting happens in the database, so this must be
   * a key the Worker allows — it silently ignores anything else. Leave it out to
   * make the column unsortable.
   *
   *   funding   : startup_name, stage, date, amount, city, country
   *   layoffs   : company, date, num_laid_off, country, industry,
   *               funding_stage, amount_raised, source
   *   investors : investor_name, funding_stage, investor_type,
   *               investment_stage, country
   */
  sortKey?: string;
};

export type FormField = {
  /** Must match the field name the API expects — see the Worker's validation schema. */
  name: string;
  label: string;
  type: "text" | "number" | "date" | "url" | "email" | "textarea" | "checkbox";
  required?: boolean;
  placeholder?: string;
  /** Shown under the input. Use for rules the API enforces. */
  help?: string;
};

export type Dataset = {
  id: DatasetId;
  /** Sidebar label. */
  title: string;
  /** Sidebar second line. */
  subtitle: string;
  icon: LucideIcon;
  /** In-app route. */
  path: string;
  /** API path, appended to VITE_API_BASE_URL. */
  endpoint: string;
  pageTitle: string;
  pageDescription: string;
  seoTitle: string;
  seoDescription: string;
  /**
   * The matching server-rendered page, if there is one. These are the pages
   * Google indexes; the app links to them so crawlers can find them.
   */
  publicPage?: string;
  /** Whether the endpoint accepts ?search= and cursor paging. */
  supportsSearch: boolean;
  supportsPaging: boolean;
  /** Sent as ?sortBy= on first load. */
  defaultSort?: string;
  columns: Column[];
  /** Empty means this section has no admin create form. */
  formFields: FormField[];
};

const linkClass = "block truncate text-brand hover:underline";

function externalLink(url: string | null | undefined, label?: string): ReactNode {
  if (!url) return "—";
  const href = url.startsWith("http") ? url : `https://${url}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
      {label ?? hostname(url)}
    </a>
  );
}

export const DATASETS: Dataset[] = [
  {
    id: "funding",
    title: "Funding",
    subtitle: "Rounds & investors",
    icon: TrendingUp,
    path: "/app/funding",
    endpoint: "/funding",
    pageTitle: "Startup funding rounds",
    pageDescription: "Every tracked round with stage, amount, investors and country.",
    seoTitle: "Startup Funding Rounds — Live Database of Raises & Investors | Cone",
    seoDescription:
      "Search startup funding rounds by stage, country, industry and investor. Updated daily.",
    publicPage: "/funding",
    supportsSearch: true,
    supportsPaging: true,
    defaultSort: "date",
    columns: [
      {
        key: "company_name",
        header: "Startup",
        width: "w-56",
        sortKey: "startup_name",
        render: (r) => (
          <CompanyCell name={String(r.company_name ?? "")} website={r.website as string} />
        ),
      },
      {
        key: "stage",
        header: "Stage",
        width: "w-32",
        sortKey: "stage",
        render: (r) => <Pills value={r.stage} kind="stage" />,
      },
      {
        key: "funding_date",
        header: "Date",
        width: "w-32",
        sortKey: "date",
        render: (r) => formatDate(r.funding_date as string),
      },
      {
        key: "amount_display",
        header: "Amount",
        width: "w-32",
        align: "right",
        sortKey: "amount",
        render: (r) =>
          (r.amount_display as string) || formatUsd(r.amount_usd as number | null),
      },
      {
        key: "industry",
        header: "Industry",
        width: "w-48",
        render: (r) => <Pills value={r.industries_arr ?? r.industry} kind="industry" />,
      },
      {
        key: "investors",
        header: "Investors",
        width: "w-64",
        render: (r) => <Pills value={r.investors_arr ?? r.investors} kind="investors" />,
      },
      {
        key: "country",
        header: "Country",
        width: "w-40",
        sortKey: "country",
        render: (r) => orDash(r.country),
      },
      {
        key: "website",
        header: "Website",
        width: "w-40",
        render: (r) => externalLink(r.website as string),
      },
    ],
    formFields: [
      { name: "company_name", label: "Startup name", type: "text", required: true },
      {
        name: "stage",
        label: "Stage",
        type: "text",
        required: true,
        placeholder: "Series A",
      },
      {
        name: "funding_date",
        label: "Funding date",
        type: "date",
        required: true,
        help: "Stored as YYYY-MM-DD.",
      },
      {
        name: "amount_display",
        label: "Amount (as shown)",
        type: "text",
        placeholder: "$38,000,000",
      },
      { name: "amount_usd", label: "Amount in USD (number)", type: "number" },
      { name: "industry", label: "Industry", type: "text", placeholder: "FinTech, SaaS" },
      { name: "city", label: "City", type: "text" },
      { name: "country", label: "Country", type: "text" },
      { name: "investors", label: "Investors", type: "textarea", help: "Comma separated." },
      { name: "description", label: "Description", type: "textarea" },
      { name: "website", label: "Website", type: "url" },
    ],
  },

  {
    id: "layoffs",
    title: "Layoff tracker",
    subtitle: "Workforce cuts",
    icon: TrendingDown,
    path: "/app/layoffs",
    endpoint: "/layoffs",
    pageTitle: "Startup layoffs",
    pageDescription: "Every tech and startup layoff since 2020, with sources.",
    seoTitle: "Startup Layoffs — Live Tracker of Tech & Startup Job Cuts | Cone",
    seoDescription:
      "Track tech and startup layoffs by company, country, industry and month, with sources.",
    publicPage: "/layoffs",
    supportsSearch: true,
    supportsPaging: true,
    defaultSort: "date",
    columns: [
      {
        key: "company",
        header: "Company",
        width: "w-56",
        sortKey: "company",
        render: (r) => <CompanyCell name={String(r.company ?? "")} />,
      },
      {
        key: "num_laid_off",
        header: "Laid off",
        width: "w-28",
        align: "right",
        sortKey: "num_laid_off",
        render: (r) => formatCount(r.num_laid_off as number | null),
      },
      {
        key: "date",
        header: "Date",
        width: "w-32",
        sortKey: "date",
        render: (r) => formatDate(r.date as string),
      },
      {
        key: "industry",
        header: "Industry",
        width: "w-40",
        sortKey: "industry",
        render: (r) => <Pills value={r.industry} kind="industry" />,
      },
      {
        key: "funding_stage",
        header: "Stage",
        width: "w-32",
        sortKey: "funding_stage",
        render: (r) => <Pills value={r.funding_stage} kind="stage" />,
      },
      {
        key: "amount_raised",
        header: "Raised",
        width: "w-28",
        align: "right",
        sortKey: "amount_raised",
        render: (r) => orDash(r.amount_raised),
      },
      {
        key: "country",
        header: "Country",
        width: "w-40",
        sortKey: "country",
        render: (r) => orDash(r.country),
      },
      {
        key: "source",
        header: "Source",
        width: "w-32",
        render: (r) => externalLink(r.source as string, "Source"),
      },
    ],
    formFields: [
      { name: "company", label: "Company", type: "text", required: true },
      { name: "date", label: "Date", type: "date", required: true },
      { name: "num_laid_off", label: "People laid off", type: "number" },
      { name: "industry", label: "Industry", type: "text" },
      { name: "country", label: "Country", type: "text" },
      { name: "funding_stage", label: "Funding stage", type: "text" },
      { name: "amount_raised", label: "Amount raised", type: "text" },
      { name: "source", label: "Source URL", type: "url", help: "Link to the news report." },
    ],
  },

  {
    id: "investors",
    title: "Investor tracker",
    subtitle: "VCs, angels & funds",
    icon: Landmark,
    path: "/app/investors",
    endpoint: "/investors",
    pageTitle: "Investor index",
    pageDescription: "Funds, angels and their stage and cheque size.",
    seoTitle: "Investor Index — VCs, Angels & Funds | Cone",
    seoDescription: "Browse investors by type, stage, cheque size and country.",
    publicPage: "/investors",
    supportsSearch: true,
    supportsPaging: true,
    columns: [
      {
        key: "investor_name",
        header: "Investor",
        width: "w-60",
        sortKey: "investor_name",
        render: (r) => (
          <CompanyCell name={String(r.investor_name ?? "")} website={r.website as string} />
        ),
      },
      {
        key: "investor_type",
        header: "Type",
        width: "w-36",
        sortKey: "investor_type",
        render: (r) => <Pills value={r.investor_type} kind="investors" />,
      },
      {
        key: "investment_stage",
        header: "Stage",
        width: "w-36",
        sortKey: "investment_stage",
        render: (r) => <Pills value={r.investment_stage} kind="stage" />,
      },
      {
        key: "investment_size",
        header: "Cheque size",
        width: "w-36",
        render: (r) => orDash(r.investment_size),
      },
      { key: "country", header: "Country", width: "w-40", render: (r) => orDash(r.country) },
      {
        key: "website",
        header: "Website",
        width: "w-40",
        render: (r) => externalLink(r.website as string),
      },
      {
        key: "linkedin_url",
        header: "LinkedIn",
        width: "w-32",
        render: (r) => externalLink(r.linkedin_url as string, "Profile"),
      },
    ],
    formFields: [
      { name: "investor_name", label: "Investor name", type: "text", required: true },
      { name: "investor_type", label: "Type", type: "text", placeholder: "VC, Angel, Fund" },
      { name: "investment_stage", label: "Investment stage", type: "text" },
      { name: "investment_size", label: "Cheque size", type: "text" },
      { name: "funding_stage", label: "Funding stage", type: "text" },
      { name: "country", label: "Country", type: "text" },
      { name: "website", label: "Website", type: "url" },
      { name: "contact_email", label: "Contact email", type: "email" },
      { name: "linkedin_url", label: "LinkedIn URL", type: "url" },
    ],
  },

  {
    id: "jobs",
    title: "Job board",
    subtitle: "Open startup roles",
    icon: Briefcase,
    path: "/app/jobs",
    endpoint: "/jobs",
    pageTitle: "Startup job board",
    pageDescription: "Open roles at startups, with location, mode and salary.",
    seoTitle: "Startup Jobs — Open Roles at Startups | Cone",
    seoDescription: "Browse open startup roles by department, location and work mode.",
    supportsSearch: false,
    supportsPaging: false,
    columns: [
      { key: "title", header: "Role", width: "w-56" },
      {
        key: "company",
        header: "Company",
        width: "w-52",
        render: (r) => (
          <CompanyCell name={String(r.company ?? "")} website={r.website as string} />
        ),
      },
      { key: "department", header: "Team", width: "w-36", render: (r) => orDash(r.department) },
      { key: "type", header: "Type", width: "w-28", render: (r) => orDash(r.type) },
      { key: "mode", header: "Mode", width: "w-28", render: (r) => orDash(r.mode) },
      { key: "location", header: "Location", width: "w-36", render: (r) => orDash(r.location) },
      { key: "salary", header: "Salary", width: "w-32", render: (r) => orDash(r.salary) },
      {
        key: "apply_link",
        header: "Apply",
        width: "w-28",
        render: (r) => externalLink((r.apply_link as string) || (r.website as string), "Apply"),
      },
    ],
    formFields: [
      { name: "title", label: "Role title", type: "text", required: true },
      { name: "company", label: "Company", type: "text", required: true },
      { name: "department", label: "Team", type: "text", placeholder: "Engineering" },
      { name: "type", label: "Employment type", type: "text", placeholder: "Full-time" },
      { name: "mode", label: "Work mode", type: "text", placeholder: "Remote" },
      { name: "location", label: "Location", type: "text" },
      { name: "salary", label: "Salary", type: "text" },
      { name: "website", label: "Company website", type: "url" },
      { name: "apply_link", label: "Apply link", type: "url" },
      { name: "tags", label: "Tags", type: "text", help: "Comma separated." },
      { name: "description", label: "Description", type: "textarea" },
      { name: "featured", label: "Feature this role", type: "checkbox" },
    ],
  },

  {
    id: "reports",
    title: "Reports",
    subtitle: "Venture research",
    icon: FileText,
    path: "/app/reports",
    endpoint: "/reports",
    pageTitle: "Venture reports",
    pageDescription: "Research reports and market maps.",
    seoTitle: "Venture Reports & Startup Research | Cone",
    seoDescription: "A library of venture research reports and market maps.",
    supportsSearch: false,
    supportsPaging: false,
    columns: [
      { key: "title", header: "Report", width: "w-80" },
      {
        key: "publisher",
        header: "Publisher",
        width: "w-52",
        render: (r) =>
          r.publisher ? <CompanyCell name={String(r.publisher)} /> : "—",
      },
      { key: "year", header: "Year", width: "w-24", render: (r) => orDash(r.year) },
      {
        key: "industry",
        header: "Industry",
        width: "w-40",
        render: (r) => <Pills value={r.industry} kind="industry" />,
      },
      { key: "country", header: "Country", width: "w-36", render: (r) => orDash(r.country) },
      {
        key: "url",
        header: "Open",
        width: "w-28",
        render: (r) => externalLink(r.url as string, "Open"),
      },
    ],
    formFields: [
      { name: "title", label: "Report title", type: "text", required: true },
      {
        name: "url",
        label: "Google Drive URL",
        type: "url",
        required: true,
        help: "The API only accepts drive.google.com or docs.google.com links.",
      },
      { name: "publisher", label: "Publisher", type: "text" },
      { name: "year", label: "Year", type: "text" },
      { name: "industry", label: "Industry", type: "text" },
      { name: "country", label: "Country", type: "text" },
      { name: "summary", label: "Summary", type: "textarea" },
      { name: "thumb", label: "Thumbnail URL", type: "url" },
    ],
  },

];

export function findDataset(id: string | undefined): Dataset | undefined {
  return DATASETS.find((dataset) => dataset.id === id);
}
